// MINI Bingo: neun Fotoaufgaben in einem 3x3-Raster.
//
// Diese Datei ist die EINZIGE Quelle fuer Kategorien, Level und Bingo-Linien.
// Sie enthaelt bewusst keinen Firebase-Import - alles hier ist rein und aus
// Jest testbar. Backend-Spiegel: functions/shared/miniBingo.js (dort nur die
// IDs, die die Server-Validierung braucht); der Sync-Test
// src/__tests__/miniBingoSync.test.js haelt beide zusammen.

/**
 * Die neun Felder in fester Rasterreihenfolge.
 *
 * `position` ist die Stelle im 3x3-Raster (1-9) und NICHT beliebig: Das
 * Lieblingsfoto liegt auf 5 und bildet damit dauerhaft die Mitte - es ist Teil
 * beider Diagonalen und der mittleren Reihe/Spalte, haengt also in vier der
 * acht moeglichen Bingo-Linien. Wer die Liste umsortiert, veraendert damit
 * auch die Gewichtung des Spiels.
 *
 * `shortTitle` ist die Beschriftung auf der Karte: Auf einem 360px-Display ist
 * ein Feld rund 100px breit, "MINI trifft MINI" passt dort nicht. Der volle
 * Titel steht im Upload- und im Bildfenster.
 */
export const MINI_BINGO_CATEGORIES = [
  {
    id: 'racetrack',
    position: 1,
    title: 'Rennstrecke',
    shortTitle: 'Rennstrecke',
    icon: '🏁',
    description: 'Dein MINI auf oder an einer Rennstrecke.',
  },
  {
    id: 'washday',
    position: 2,
    title: 'Waschtag',
    shortTitle: 'Waschtag',
    icon: '🧽',
    description: 'Dein MINI beim Waschen oder frisch gewaschen.',
  },
  {
    id: 'meetup',
    position: 3,
    title: 'MINI Treffen',
    shortTitle: 'Treffen',
    icon: '🚗',
    description: 'Dein MINI bei einem Treffen oder Club-Event.',
  },
  {
    id: 'fuelstop',
    position: 4,
    title: 'Tankstopp',
    shortTitle: 'Tankstopp',
    icon: '⛽',
    description: 'Dein MINI beim Tanken oder Laden.',
  },
  {
    id: 'favorite',
    position: 5,
    title: 'Lieblingsfoto',
    shortTitle: 'Liebling',
    icon: '❤️',
    description: 'Dein persönliches Lieblingsbild deines MINI.',
  },
  {
    id: 'garage',
    position: 6,
    title: 'Werkstatt',
    shortTitle: 'Werkstatt',
    icon: '🔧',
    description: 'Dein MINI in der Werkstatt, beim Schrauben oder Umbauen.',
  },
  {
    id: 'tour',
    position: 7,
    title: 'Auf Tour',
    shortTitle: 'Auf Tour',
    icon: '🛣️',
    description: 'Dein MINI während einer gemeinsamen oder persönlichen Tour.',
  },
  {
    id: 'scenery',
    position: 8,
    title: 'Schöne Kulisse',
    shortTitle: 'Kulisse',
    icon: '🌄',
    description: 'Dein MINI vor einer besonderen Landschaft oder Location.',
  },
  {
    id: 'minimeetsmini',
    position: 9,
    title: 'MINI trifft MINI',
    shortTitle: 'MINI x MINI',
    icon: '🚘',
    description: 'Dein MINI gemeinsam mit mindestens einem anderen MINI.',
  },
];

/**
 * Das aktuell einzige Set.
 *
 * Es gibt bewusst KEINE Oberflaeche fuer mehrere Sets - nur die Struktur, die
 * ein zweites spaeter nicht ausschliesst. Die Komponenten lesen ihre
 * Kategorien aus einem uebergebenen Set, nicht aus dem Modul-Export; ein
 * "Sommer-Bingo" waere damit ein weiteres Objekt hier plus ein eigenes Feld am
 * Nutzerdokument, keine Umbauarbeit an der Anzeige.
 */
export const MINI_BINGO_SET = {
  id: 'standard2026',
  title: 'MINI Bingo',
  categories: MINI_BINGO_CATEGORIES,
};

/** Feld am Nutzerdokument, unter dem die Eintraege dieses Sets liegen. */
export const MINI_BINGO_FIELD = 'miniBingo';

export const MINI_BINGO_SIZE = MINI_BINGO_CATEGORIES.length;

/**
 * Alle acht Linien als Rasterpositionen. Position 5 kommt in vier davon vor -
 * siehe Kommentar an den Kategorien.
 */
export const MINI_BINGO_LINES = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
  [1, 4, 7],
  [2, 5, 8],
  [3, 6, 9],
  [1, 5, 9],
  [3, 5, 7],
];

/**
 * Art einer Linie: waagerecht, senkrecht oder diagonal.
 *
 * Wird aus den Positionen ABGELEITET statt an MINI_BINGO_LINES gepflegt - eine
 * zweite Liste in derselben Reihenfolge waere genau die Art von Dublette, die
 * beim naechsten Umsortieren still falsch wird.
 *
 * Wozu das ueberhaupt: Die Anzeige sagte "Bereits 6 volle Reihen geschafft"
 * und meinte damit alle acht moeglichen Linien. Christian hat nachgezaehlt,
 * kam auf 4 (zwei waagerecht, zwei senkrecht) und meldete die Zahl als falsch
 * (09.09.). Sie stimmte - die beiden Diagonalen ueber die Mitte fehlten ihm,
 * weil "Reihe" sie nicht meint. Die Zahl war richtig, das Wort war es nicht.
 */
export function lineKind(line) {
  const first = line[0];
  if (line.every((p) => Math.ceil(p / 3) === Math.ceil(first / 3))) return 'row';
  if (line.every((p) => (p - 1) % 3 === (first - 1) % 3)) return 'column';
  return 'diagonal';
}

/** Ein- und Mehrzahl je Linienart. */
export const MINI_BINGO_LINE_KIND_LABELS = {
  row: ['Reihe', 'Reihen'],
  column: ['Spalte', 'Spalten'],
  diagonal: ['Diagonale', 'Diagonalen'],
};

/** Zaehlt vollstaendige Linien nach Art. */
export function countLineKinds(lines) {
  const out = { row: 0, column: 0, diagonal: 0 };
  for (const line of lines ?? []) out[lineKind(line)] += 1;
  return out;
}

/** "2 Reihen, 2 Spalten und 2 Diagonalen" - Arten ohne Treffer fallen weg. */
export function describeLineKinds(kinds) {
  const parts = ['row', 'column', 'diagonal']
    .filter((k) => (kinds?.[k] ?? 0) > 0)
    .map((k) => `${kinds[k]} ${MINI_BINGO_LINE_KIND_LABELS[k][kinds[k] === 1 ? 0 : 1]}`);

  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1]}`;
}

/**
 * Status nach Anzahl gefuellter Felder. `min`/`max` sind einschliesslich.
 * Zentral hier, damit die Schwelle nicht in drei Komponenten getrennt driftet.
 */
export const MINI_BINGO_LEVELS = [
  { id: 'starter', min: 0, max: 2, title: 'MINI Starter' },
  { id: 'explorer', min: 3, max: 5, title: 'MINI Explorer' },
  { id: 'enthusiast', min: 6, max: 8, title: 'MINI Enthusiast' },
  { id: 'thrillseeker', min: 9, max: 9, title: 'MINI Thrill Seeker' },
];

/**
 * Grenzen fuer das hochgeladene Bild.
 *
 * 1600px lange Kante: Auf einer Karte von hoechstens ~360px Kantenlaenge (und
 * in der Lightbox auf einem Retina-Display) ist das reichlich, und ein
 * Handyfoto schrumpft damit von 8 MB auf gut 200 KB. `maxBytes` spiegelt die
 * Storage-Rule - die Pruefung im Browser ersetzt sie nicht, sie erspart nur
 * den vergeblichen Upload. Gleiche Ueberlegung wie bei IMAGE_LIMITS.
 */
export const MINI_BINGO_IMAGE = {
  maxEdge: 1600,
  quality: 0.85,
  maxBytes: 8 * 1024 * 1024,
};

/** Kategorie zu einer ID, oder undefined. */
export function bingoCategory(categoryId, set = MINI_BINGO_SET) {
  return set.categories.find((c) => c.id === categoryId);
}

/** Kategorien in Rasterreihenfolge - unabhaengig davon, wie sie dastehen. */
export function orderedCategories(set = MINI_BINGO_SET) {
  return [...set.categories].sort((a, b) => a.position - b.position);
}

/** Ist zu dieser Kategorie ein Bild hinterlegt? */
export function hasImage(entries, categoryId) {
  return Boolean(entries?.[categoryId]?.imageUrl);
}

/** Anzahl gefuellter Felder. Wird NICHT gespeichert, sondern immer gerechnet. */
export function filledCount(entries, set = MINI_BINGO_SET) {
  return set.categories.filter((c) => hasImage(entries, c.id)).length;
}

/** Rasterpositionen der gefuellten Felder. */
export function filledPositions(entries, set = MINI_BINGO_SET) {
  return set.categories.filter((c) => hasImage(entries, c.id)).map((c) => c.position);
}

/** Vollstaendige Linien - die Linien selbst, damit die Anzeige sie zaehlen kann. */
export function completedLines(entries, set = MINI_BINGO_SET) {
  const filled = new Set(filledPositions(entries, set));
  return MINI_BINGO_LINES.filter((line) => line.every((pos) => filled.has(pos)));
}

/** Fortschritt in Prozent, gerundet. */
export function progressPercent(entries, set = MINI_BINGO_SET) {
  const total = set.categories.length;
  if (!total) return 0;
  return Math.round((filledCount(entries, set) / total) * 100);
}

/** Status zur Anzahl gefuellter Felder. Faellt nie auf undefined zurueck. */
export function levelFor(count) {
  return (
    MINI_BINGO_LEVELS.find((l) => count >= l.min && count <= l.max) ?? MINI_BINGO_LEVELS[0]
  );
}

/** Alle Felder gefuellt? */
export function isComplete(entries, set = MINI_BINGO_SET) {
  return filledCount(entries, set) === set.categories.length;
}

/**
 * Alles, was die Anzeige braucht, in einem Durchgang.
 *
 * Bewusst eine Funktion statt fuenf Aufrufen in der Komponente: So laufen
 * Fortschritt, Level und Bingo-Erkennung garantiert auf demselben Stand.
 */
export function bingoSummary(entries, set = MINI_BINGO_SET) {
  const count = filledCount(entries, set);
  const lines = completedLines(entries, set);
  return {
    count,
    total: set.categories.length,
    percent: progressPercent(entries, set),
    lines: lines.length,
    // Aufgeschluesselt, damit die Anzeige die Zahl belegen kann statt sie nur
    // zu behaupten - siehe lineKind().
    lineKinds: countLineKinds(lines),
    hasBingo: lines.length > 0,
    complete: count === set.categories.length,
    level: levelFor(count),
  };
}
